/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { format, getTelemetryObject, IModuleProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IAccountAddressTileData } from './account-address-tile.data';
import { IAccountAddressTileProps } from './account-address-tile.props.autogenerated';
import DescriptionComponent from './components/account-address-tile-description';
import LinksComponent from './components/account-address-tile-links';

export interface IAccountAddressTileViewProps extends IAccountAddressTileProps<IAccountAddressTileData> {
    AccountAddressTile: IModuleProps;
    className: string;
    heading?: React.ReactNode;
    links?: React.ReactNode;
    description?: React.ReactNode;
}

/**
 *
 * AccountAddress component.
 * @extends {React.PureComponent<IAccountAddressTileProps<IAccountAddressTileData>>}
 */
class AccountAddressTile extends React.PureComponent<IAccountAddressTileProps<IAccountAddressTileData>> {
    private readonly telemetryContent: ITelemetryContent;

    constructor(props: IAccountAddressTileProps<IAccountAddressTileData>) {
        super(props);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public render(): JSX.Element {
        const { config, resources, data } = this.props;
        const { className, heading, links } = config;
        const { accountAddressTileDescriptionForSingleAddress, accountAddressTileDescriptionForMultipleAddresses } = resources;
        const { address } = data;
        const addressCount = (address && address.result && address.result.length) || 0;
        const accountAddressTileDescription =
            addressCount === 1 ? accountAddressTileDescriptionForSingleAddress : accountAddressTileDescriptionForMultipleAddresses;
        const description = format(accountAddressTileDescription, addressCount);

        const viewProps = {
            ...this.props,
            className,
            AccountAddressTile: {
                moduleProps: this.props,
                className: classnames('ms-account-address-tile', config.className)
            },
            heading: heading && heading.text && (
                <Msdyn365.Text
                    className='ms-account-address-tile__heading'
                    tag={heading.tag || 'h2'}
                    text={heading.text}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            links: links && links.length > 0 && (
                <LinksComponent
                    {...{
                        links,
                        onTextChange: this.handleLinkTextChange,
                        requestContext: this.props.context.request,
                        telemetryContent: this.telemetryContent
                    }}
                />
            ),
            description: <DescriptionComponent description={description} />
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.heading.text = event.target.value);

    public handleLinkTextChange = (linkIndex: number) => (event: Msdyn365.ContentEditableEvent) => {
        if (this.props.config.links && this.props.config.links[linkIndex]) {
            this.props.config.links[linkIndex].linkText = event.target.value;
        }
    };
}

export default AccountAddressTile;
